import { useEffect, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell } from "recharts";
import {
  GraduationCap, IdentificationCard, CalendarBlank, Books, Exam, CreditCard,
  Sparkle, ArrowUpRight, Check, Bus,
} from "@phosphor-icons/react";

const COURSE_GRADES = [
  { code: "CS301", title: "Data Structures", grade: "A", score: 88, faculty: "Dr. Iyer" },
  { code: "MA201", title: "Linear Algebra", grade: "A-", score: 82, faculty: "Dr. Bose" },
  { code: "HU102", title: "Indian Constitution", grade: "A+", score: 94, faculty: "Dr. Shah" },
  { code: "PR101", title: "Capstone Project I", grade: "—", score: null, faculty: "Dr. Kapoor" },
];

const UPCOMING = [
  { code: "CS301", what: "Mid-Sem · Online", when: "Feb 22, 9:00 AM", room: "LH-201", kind: "exam" },
  { code: "PR101", what: "Capstone Demo", when: "Feb 24, 2:00 PM", room: "Studio-A", kind: "assignment" },
  { code: "HU102", what: "Essay due", when: "Feb 19, 11:59 PM", room: "—", kind: "assignment" },
];

const ATTENDANCE_TREND = [
  { week: "W1", pct: 96 }, { week: "W2", pct: 92 }, { week: "W3", pct: 88 },
  { week: "W4", pct: 94 }, { week: "W5", pct: 91 }, { week: "W6", pct: 95 },
];

export default function StudentDashboard() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get(`/students?institution_type=${user?.institution_type || "higher_ed"}`).then(r => setMe(r.data[0]));
  }, [user?.institution_type]);

  return (
    <div className="p-6 lg:p-8 space-y-8" data-testid="student-dashboard">
      {/* Hero */}
      <div className="grid lg:grid-cols-12 gap-5">
        <div className="lg:col-span-8 bg-[hsl(244_53%_20%)] text-white rounded-md p-6 relative overflow-hidden">
          <div className="absolute inset-0 eos-grid-lines opacity-10"/>
          <div className="relative">
            <div className="eos-overline text-white/50">Student · {me?.program || "B.Tech CSE"}</div>
            <h1 className="font-heading font-extrabold text-3xl md:text-4xl tracking-tight mt-2 leading-tight">
              Good morning, {user?.name?.split(" ")[0] || "Student"}.
            </h1>
            <p className="text-white/70 mt-2 text-sm">
              You have <b className="text-white">3 assignments</b> due this week and your next class is in
              <b className="text-[hsl(20_90%_55%)]"> 42 minutes</b>.
            </p>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { l: "CGPA", v: me?.cgpa ?? "8.92" },
                { l: "Attendance", v: `${me?.attendance ?? 92.1}%` },
                { l: "Credits earned", v: "84/160" },
                { l: "ABC ID", v: "Synced" },
              ].map(s => (
                <div key={s.l}>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">{s.l}</div>
                  <div className="font-heading font-extrabold text-2xl mt-1 tracking-tight">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-4 eos-card">
          <div className="flex items-center gap-2">
            <IdentificationCard size={18} weight="duotone" className="text-[hsl(244_53%_20%)]"/>
            <div className="eos-overline">Digital Identity</div>
          </div>
          <div className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">APAAR ID</span><span className="font-mono-eos text-[12px]">{me?.apaar_id || "APAAR-—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">DigiLocker</span><span className="text-emerald-700 inline-flex items-center gap-1"><Check size={12} weight="bold"/>Verified</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Aadhaar e-KYC</span><span className="text-emerald-700">Consent on file</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">ABC Credits</span><span className="font-mono-eos">84</span></div>
          </div>
          <a href="/app/identity" className="mt-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">Manage identity <ArrowUpRight size={11} weight="bold"/></a>
        </div>
      </div>

      {/* Upcoming */}
      <div className="grid lg:grid-cols-12 gap-5">
        <div className="lg:col-span-7 eos-card p-0 overflow-hidden">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <CalendarBlank size={16} weight="duotone"/>
            <div className="font-heading font-semibold text-base">This week</div>
          </div>
          <div className="divide-y divide-border">
            {UPCOMING.map((u) => (
              <div key={`${u.code}-${u.what}`} className="p-4 flex items-center gap-4">
                <div className={`w-10 h-10 rounded-md flex items-center justify-center
                  ${u.kind === "exam" ? "bg-[hsl(20_90%_48%)] text-white" : "bg-secondary"}`}>
                  {u.kind === "exam" ? <Exam size={18} weight="duotone"/> : <Books size={18} weight="duotone"/>}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-sm">{u.code} · {u.what}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{u.when} · {u.room}</div>
                </div>
                <Button variant="ghost" className="h-8 px-2 text-xs rounded">Details</Button>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 eos-card">
          <div className="eos-overline">Attendance · last 6 weeks</div>
          <div className="font-heading font-bold text-xl mt-1">{me?.attendance ?? 92.1}% average</div>
          <div className="h-44 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ATTENDANCE_TREND}>
                <XAxis dataKey="week" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))"/>
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" domain={[60, 100]}/>
                <Tooltip contentStyle={{ borderRadius: 6, fontSize: 12, border: "1px solid hsl(var(--border))" }}/>
                <Bar dataKey="pct" radius={[2,2,0,0]}>
                  {ATTENDANCE_TREND.map((d, i) => <Cell key={i} fill={d.pct >= 90 ? "hsl(244 53% 20%)" : "hsl(20 90% 48%)"}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Courses */}
      <div className="eos-card p-0 overflow-hidden">
        <div className="p-4 border-b border-border flex items-center gap-2">
          <GraduationCap size={16} weight="duotone"/>
          <div className="font-heading font-semibold text-base">Current courses</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-secondary text-muted-foreground">
            <tr>{["Code", "Course", "Faculty", "Score", "Grade"].map(h => (
              <th key={h} className="text-left px-4 py-2.5 text-[11px] uppercase tracking-[0.15em] font-medium">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {COURSE_GRADES.map(c => (
              <tr key={c.code} className="border-t border-border">
                <td className="px-4 py-3 font-mono-eos text-[12px]">{c.code}</td>
                <td className="px-4 py-3 font-medium">{c.title}</td>
                <td className="px-4 py-3 text-muted-foreground">{c.faculty}</td>
                <td className="px-4 py-3 font-mono-eos">{c.score ?? "—"}</td>
                <td className="px-4 py-3"><span className="text-[11px] px-2 py-1 rounded-md bg-secondary font-medium">{c.grade}</span></td>
              </tr>
            ))}          </tbody>
        </table>
      </div>

      {/* Fees + Bus quick view */}
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="eos-card">
          <div className="flex items-center gap-2"><CreditCard size={16} weight="duotone"/><div className="eos-overline">Fees</div></div>
          <div className="font-heading font-bold text-2xl mt-2">{me?.fees_status === "Paid" ? "All clear" : me?.fees_status}</div>
          <p className="text-sm text-muted-foreground mt-1">Current term tuition: ₹1,42,000 · Hostel: ₹42,000 · Library: ₹2,400.</p>
          <Button className="mt-4 rounded-md bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]">View statement</Button>
        </div>
        <div className="eos-card">
          <div className="flex items-center gap-2"><Bus size={16} weight="duotone"/><div className="eos-overline">My Bus · B-14</div></div>
          <div className="font-heading font-bold text-2xl mt-2">Arriving in 6 min</div>
          <p className="text-sm text-muted-foreground mt-1">Whitefield → Campus · Stop B · Load 42/56.</p>
          <a href="/app/campus" className="mt-3 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">Track live <ArrowUpRight size={11} weight="bold"/></a>
        </div>
      </div>

      <div className="eos-card flex items-center gap-4">
        <Sparkle size={20} weight="fill" className="text-[hsl(20_90%_48%)]"/>
        <div className="flex-1 text-sm">
          <b>Copilot tip</b>: You're 4 ABC credits short of the multi-disciplinary minor track. Add an Elective (EL220 Sanskrit Literature) before Mar 1.
        </div>
        <Button variant="outline" className="rounded-md">Explore</Button>
      </div>
    </div>
  );
}
